import { useCallback, useEffect, useRef, useState } from 'react';
import * as orgApi from '../api/organizations';
import { useOrg } from '../lib/org';
import { useToast } from './toast';
import { Spinner, ErrorAlert } from './ui';
import OrgTypePicker from './OrgTypePicker';
import ComplianceProfileForm from './ComplianceProfileForm';
import { cn, ui } from '../lib/ui';
import { orgAdminMayChangeType, orgTypePolicy } from '../lib/orgTypes';
import { fieldErrorsFrom } from '../lib/formErrors';
import { NONPROFIT_CHANGE_WARNING, afterTypeChange } from '../lib/typeChange';

/**
 * Org settings -> Organization type (docs/Decisions.md D64): the type, while
 * it can still be changed, and the nonprofit or investment profile with its
 * review state.
 */
export default function OrgTypeSettings() {
  const { activeOrg, refresh } = useOrg();
  const toast = useToast();
  const [org, setOrg] = useState(null);
  const [error, setError] = useState(null);
  const [choice, setChoice] = useState('');
  const [saving, setSaving] = useState(false);
  const [typeError, setTypeError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await orgApi.getOrg(activeOrg.orgId);
      const loaded = data.organization || data;
      setOrg(loaded);
      setChoice(loaded.orgType || '');
    } catch (err) {
      setError(err);
    }
  }, [activeOrg.orgId]);

  useEffect(() => {
    load();
  }, [load]);

  // The Payments page links here with #org-type; the router does not scroll to a hash.
  const sectionRef = useRef(null);
  useEffect(() => {
    if (org && window.location.hash === '#org-type') {
      sectionRef.current?.scrollIntoView({ block: 'start' });
    }
  }, [org]);

  async function saveType() {
    setSaving(true);
    setTypeError(null);
    try {
      const from = org.orgType;
      const data = await orgApi.updateOrganization(org.orgId, { orgType: choice });
      setOrg(data.organization);
      toast.success('Organization type saved');
      const note = await afterTypeChange(org.orgId, from, choice);
      if (note) toast.info(note);
      refresh();
    } catch (err) {
      setTypeError(fieldErrorsFrom(err).orgType || err.message || 'Failed to save the type');
    } finally {
      setSaving(false);
    }
  }

  if (error) return <ErrorAlert error={error} />;
  if (!org) return <Spinner label="Loading organization type..." />;

  const policy = orgTypePolicy(org.orgType);
  const canChange = orgAdminMayChangeType(org);

  return (
    <section className="mt-8 scroll-mt-header" id="org-type" ref={sectionRef}>
      <h2 className={ui.h2}>Organization type</h2>
      <div className={cn(ui.card, 'mb-4')}>
        {!policy && (
          <div className={cn(ui.alertWarning, 'mb-4')}>
            Choose your organization type. Until you do, payments that depend on it (such as
            donations) stay off.
          </div>
        )}
        {canChange ? (
          <>
            <OrgTypePicker value={choice} onChange={setChoice} />
            {org.orgType === 'nonprofit' && choice && choice !== 'nonprofit' && (
              <p className={cn(ui.alertWarning, 'mt-3')}>{NONPROFIT_CHANGE_WARNING}</p>
            )}
            {typeError && (
              <span className={ui.fieldError} role="alert">
                {typeError}
              </span>
            )}
            <div className={cn(ui.row, 'mt-4')}>
              <button
                className={ui.btnPrimary}
                type="button"
                disabled={saving || !choice || choice === org.orgType}
                onClick={saveType}
              >
                {saving ? 'Saving...' : 'Save type'}
              </button>
            </div>
            <p className={cn(ui.hint, 'mb-0')}>
              You can change the type until your organization has members; after that the platform
              team changes it.
            </p>
          </>
        ) : (
          <p className="m-0">
            <strong>{policy.label}.</strong> {policy.summary}{' '}
            <span className={ui.muted}>
              Your organization has members, so the platform team changes its type.
            </span>
          </p>
        )}
      </div>

      {policy?.profile && (
        <>
          <h3 className={ui.h3}>
            {policy.profile === 'nonprofit' ? 'Nonprofit profile' : 'Compliance profile'}
          </h3>
          <div className={ui.card}>
            <ComplianceProfileForm
              key={`${org.orgType}-${org.compliance?.status || 'none'}`}
              org={org}
              onSaved={updated => setOrg(updated)}
            />
          </div>
        </>
      )}
    </section>
  );
}
